"use client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getAllNews } from "../../../services/newsServices";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import { NewsListTable } from "../../../components/admin/news/NewsListTable";
import Pagination from "../../../components/admin/common/Pagination";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";
import { getCookie } from "../../../utils";

const PendingNews = () => {
  const [entriesOption, setEntriesOption] = useState({
    value: 10,
    label: "10",
  });
  const [lastIds, setLastIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [newsList, setNewsList] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const currentLastId =
    currentIndex > 0 ? lastIds[currentIndex - 1] : undefined;

  const newsFilterStatus = "approved";

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      "pending-news",
      newsFilterStatus,
      currentLastId,
      entriesOption.value,
    ],
    queryFn: () =>
      getAllNews({
        status: newsFilterStatus,
        lastId: currentLastId,
        limit: entriesOption.value,
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (data?.data?.items) {
      setNewsList(data.data.items);
      setHasMore(data.data.hasMore);
    }
  }, [data]);

  const handleNext = () => {
    if (hasMore && data?.data?.nextCursor) {
      const updated = [...lastIds];
      updated[currentIndex] = data.data.nextCursor;
      setLastIds(updated);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setCurrentIndex(0);
    setLastIds([]);
  };

  const role = getCookie("role");
  const userId = getCookie("userId");

  const { data: permissionsData } = useUserModulePermissions("News", userId, {
    enabled: role === "maintainer" && userId && userId !== "undefined",
  });

  return (
    <div className="flex h-screen p-4 gap-4 bg-white">
      {/* Fixed Sidebar */}
      <div className="flex-shrink-0">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                <Header
                  title="News List"
                  desc="Review, approve, or reject submitted news articles before publication"
                />

                <div className="mb-4">
                  <DropdownSelect
                    selected={entriesOption}
                    onChange={handleEntriesChange}
                  />
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <p>Loading news...</p>
                  </div>
                ) : isError ? (
                  <div className="flex items-center justify-center py-8">
                    <p className="text-red-500">Error fetching news</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <NewsListTable
                      news={newsList}
                      refetch={refetch}
                      startIndex={currentIndex * entriesOption.value}
                      permissionsData={permissionsData?.data ?? null}
                    />
                    <div className="pb-4">
                      <Pagination
                        currentPage={currentIndex + 1}
                        hasMore={hasMore}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default PendingNews;
